import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm, Field } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { ensureCurrentUser } from '../../util/data';
import {
  isChangeEmailTakenError,
  isChangeEmailWrongPassword,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import {
  Form,
  Button,
  FieldTextInput,
  FieldPhoneNumberInput,
  ToggleSwitch,
} from '../../components';

import css from './AccountContactForm.css';

const SHOW_EMAIL_SENT_TIMEOUT = 2000;

class AccountContactFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showVerificationEmailSentMessage: false,
    };
    this.emailSentTimeoutId = null;
    this.handleResendVerificationEmail = this.handleResendVerificationEmail.bind(this);
    this.submittedValues = {};
  }

  componentWillUnmount() {
    window.clearTimeout(this.emailSentTimeoutId);
  }

  handleResendVerificationEmail() {
    this.setState({ showVerificationEmailSentMessage: true });

    this.props.onResendVerificationEmail().then(() => {
      this.emailSentTimeoutId = window.setTimeout(() => {
        this.setState({ showVerificationEmailSentMessage: false });
      }, SHOW_EMAIL_SENT_TIMEOUT);
    });
  }

  render() {
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            rootClassName,
            className,
            saveEmailError,
            savePhoneNumberError,
            currentUser,
            formId,
            handleSubmit,
            inProgress,
            intl,
            invalid,
            sendVerificationEmailError,
            sendVerificationEmailInProgress,
            values,
          } = fieldRenderProps;
          const { email, phoneNumber, textMessageNotifications } = values;

          const user = ensureCurrentUser(currentUser);
          if (!user.id) {
            return null;
          }

          const { email: currentEmail, emailVerified, pendingEmail, profile } = user.attributes;

          const emailChanged = currentEmail !== email;

          const emailLabel = intl.formatMessage({ id: 'ContactDetailsForm.emailLabel' });
          const emailPlaceholder = currentEmail || '';
          const emailRequired = validators.required(
            intl.formatMessage({ id: 'ContactDetailsForm.emailRequired' })
          );
          const emailValid = validators.emailFormatValid(
            intl.formatMessage({ id: 'ContactDetailsForm.emailInvalid' })
          );

          const tooManyVerificationRequests = isTooManyEmailVerificationRequestsError(
            sendVerificationEmailError
          );

          const emailTouched = this.submittedValues.email !== values.email;
          const emailTakenErrorText = isChangeEmailTakenError(saveEmailError)
            ? intl.formatMessage({ id: 'ContactDetailsForm.emailTakenError' })
            : null;

          let resendEmailMessage = null;
          if (tooManyVerificationRequests) {
            resendEmailMessage = (
              <span className={css.tooMany}>
                <FormattedMessage id="ContactDetailsForm.tooManyVerificationRequests" />
              </span>
            );
          } else if (
            sendVerificationEmailInProgress ||
            this.state.showVerificationEmailSentMessage
          ) {
            resendEmailMessage = (
              <span className={css.emailSent}>
                <FormattedMessage id="ContactDetailsForm.emailSent" />
              </span>
            );
          } else {
            /* eslint-disable jsx-a11y/no-static-element-interactions */
            resendEmailMessage = (
              <span
                className={css.helperLink}
                onClick={this.handleResendVerificationEmail}
                role="button"
              >
                <FormattedMessage id="ContactDetailsForm.resendEmailVerificationText" />
              </span>
            );
            /* eslint-enable jsx-a11y/no-static-element-interactions */
          }

          let emailVerifiedInfo = null;
          if (emailVerified && !pendingEmail && !emailChanged) {
            emailVerifiedInfo = (
              <span className={css.emailVerified}>
                <FormattedMessage id="ContactDetailsForm.emailVerified" />
              </span>
            );
          } else if (!emailVerified && !pendingEmail) {
            emailVerifiedInfo = (
              <span className={css.emailUnverified}>
                <FormattedMessage
                  id="ContactDetailsForm.emailUnverified"
                  values={{ resendEmailMessage }}
                />
              </span>
            );
          } else if (pendingEmail) {
            const pendingEmailStyled = <span className={css.emailStyle}>{pendingEmail}</span>;
            const pendingEmailCheckInbox = (
              <span className={css.checkInbox}>
                <FormattedMessage
                  id="ContactDetailsForm.pendingEmailCheckInbox"
                  values={{ pendingEmail: pendingEmailStyled }}
                />
              </span>
            );
            emailVerifiedInfo = (
              <span className={css.pendingEmailUnverified}>
                <FormattedMessage
                  id="ContactDetailsForm.pendingEmailUnverified"
                  values={{ pendingEmailCheckInbox, resendEmailMessage }}
                />
              </span>
            );
          }

          // phone
          const protectedData = profile.protectedData || {};
          const currentPhoneNumber = protectedData.phoneNumber;
          const phoneNumberChanged = currentPhoneNumber !== phoneNumber;

          const phoneNumberLabel = intl.formatMessage({ id: 'SignupForm.phoneNumberLabel' });
          const phoneNumberRequired = validators.phoneNumberRequired(
            intl.formatMessage({ id: 'SignupForm.phoneNumberRequired' })
          );

          const currentTextMessageNotifications = protectedData?.textMessageNotifications;
          const textMessageNotificationsChanged =
            currentTextMessageNotifications !== textMessageNotifications;

          // password (only when changing email)
          const passwordLabel = intl.formatMessage({ id: 'ContactDetailsForm.passwordLabel' });
          const passwordPlaceholder = intl.formatMessage({
            id: 'ContactDetailsForm.passwordPlaceholder',
          });
          const passwordRequired = validators.requiredStringNoTrim(
            intl.formatMessage({ id: 'ContactDetailsForm.passwordRequired' })
          );
          const passwordMinLength = validators.minLength(
            intl.formatMessage(
              { id: 'ContactDetailsForm.passwordTooShort' },
              { minLength: validators.PASSWORD_MIN_LENGTH }
            ),
            validators.PASSWORD_MIN_LENGTH
          );
          const passwordValidators = emailChanged
            ? validators.composeValidators(passwordRequired, passwordMinLength)
            : null;

          const passwordFailedMessage = intl.formatMessage({
            id: 'ContactDetailsForm.passwordFailed',
          });
          const passwordTouched = this.submittedValues.currentPassword !== values.currentPassword;
          const passwordErrorText = isChangeEmailWrongPassword(saveEmailError)
            ? passwordFailedMessage
            : null;

          const isGenericEmailError = saveEmailError && !(emailTakenErrorText || passwordErrorText);
          let genericError = null;
          if (isGenericEmailError && savePhoneNumberError) {
            genericError = (
              <span className={css.error}>
                <FormattedMessage id="ContactDetailsForm.genericFailure" />
              </span>
            );
          } else if (isGenericEmailError) {
            genericError = (
              <span className={css.error}>
                <FormattedMessage id="ContactDetailsForm.genericEmailFailure" />
              </span>
            );
          } else if (savePhoneNumberError) {
            genericError = (
              <span className={css.error}>
                <FormattedMessage id="ContactDetailsForm.genericPhoneNumberFailure" />
              </span>
            );
          }

          const classes = classNames(rootClassName || css.root, className);
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid ||
            pristineSinceLastSubmit ||
            inProgress ||
            !(emailChanged || phoneNumberChanged || textMessageNotificationsChanged);

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.fields}>
                <FieldTextInput
                  type="email"
                  name="email"
                  id={formId ? `${formId}.email` : 'email'}
                  label={emailLabel}
                  placeholder={emailPlaceholder}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                  customErrorText={emailTouched ? null : emailTakenErrorText}
                />
                {emailVerifiedInfo}
                {emailChanged ? (
                  <FieldTextInput
                    className={css.password}
                    type="password"
                    name="currentPassword"
                    id={formId ? `${formId}.currentPassword` : 'currentPassword'}
                    autoComplete="current-password"
                    label={passwordLabel}
                    placeholder={passwordPlaceholder}
                    validate={passwordValidators}
                    customErrorText={passwordTouched ? null : passwordErrorText}
                  />
                ) : null}

                <FieldPhoneNumberInput
                  type="text"
                  id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                  name="phoneNumber"
                  label={phoneNumberLabel}
                  validate={phoneNumberRequired}
                  warning
                />

                <div className={css.toggleRow}>
                  <Field name="textMessageNotifications" type="checkbox">
                    {({ input }) => (
                      <ToggleSwitch
                        isOn={!!input.checked}
                        onToggle={next => input.onChange(next)}
                        label={intl.formatMessage({ id: 'ContactDetailsForm.notificationLabel' })}
                      />
                    )}
                  </Field>
                  <p className={css.toggleHelp}>
                    <FormattedMessage id="AccountSettingsPage.textMessageNotificationsHelp" />
                  </p>
                </div>
              </div>

              <div className={css.bottomWrapper}>
                {genericError}
                <Button
                  type="submit"
                  className={css.submitButton}
                  inProgress={inProgress}
                  ready={pristineSinceLastSubmit}
                  disabled={submitDisabled}
                >
                  <FormattedMessage id="AccountSettingsPage.saveChanges" />
                </Button>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

AccountContactFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  saveEmailError: null,
  savePhoneNumberError: null,
  inProgress: false,
  sendVerificationEmailError: null,
  sendVerificationEmailInProgress: false,
};

const { bool, func, string } = PropTypes;

AccountContactFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  saveEmailError: propTypes.error,
  savePhoneNumberError: propTypes.error,
  inProgress: bool,
  intl: intlShape.isRequired,
  onResendVerificationEmail: func.isRequired,
  ready: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  sendVerificationEmailInProgress: bool,
};

const AccountContactForm = compose(injectIntl)(AccountContactFormComponent);
AccountContactForm.displayName = 'AccountContactForm';

export default AccountContactForm;
